import { getEndpoints } from "../../Lib/Config/EndPoints/EndPoints";
import { apiRequest } from "../../Lib/Utils/ApiRequest";
import { IApiResponse } from "../../Orchestrator/Types/Index";
import {
    ICorporateInformation,
    ICorporateOk,
    ICorporateResponse,
    ICorporateUsersPage,
    ICorporateUsersUpdate,
} from "../../Orchestrator/Types/Interface/ICorporate";

export async function getCorporate(
    corporateId: number
): Promise<IApiResponse<ICorporateResponse>> {
    const endpoints = await getEndpoints();
    return await apiRequest<ICorporateResponse>(
        endpoints.getCorporate(corporateId),
        "GET"
    );
}

/**
 * `information` must be the COMPLETE Corporate Information object.
 *
 * Not because the schema demands it — 2.0.5 declares no required fields — but
 * because this endpoint REPLACES rather than merges: anything left out is set
 * to null. Measured on INT, where omitting `contract` nulled it irreversibly.
 * Build the body with buildCorporateUpdateBody / buildCardRemovalBody, which
 * start from the loaded object, never by hand.
 */
export async function updateCorporate(
    corporateId: number,
    information: ICorporateInformation
): Promise<IApiResponse<ICorporateOk>> {
    const endpoints = await getEndpoints();
    return await apiRequest<ICorporateOk>(
        endpoints.updateCorporate(corporateId),
        "PUT",
        { body: JSON.stringify(information) }
    );
}

/** The contract caps `size` at 100; ask for it rather than taking the
 *  backend default and silently truncating a larger company. */
const MEMBERS_PAGE_SIZE = 100;

export async function getCorporateUsers(
    corporateId: number
): Promise<IApiResponse<ICorporateUsersPage>> {
    const endpoints = await getEndpoints();
    return await apiRequest<ICorporateUsersPage>(
        `${endpoints.getCorporateUsers(corporateId)}?page=0&size=${MEMBERS_PAGE_SIZE}`,
        "GET"
    );
}

export async function updateCorporateUsers(
    corporateId: number,
    update: ICorporateUsersUpdate
): Promise<IApiResponse<ICorporateOk>> {
    const endpoints = await getEndpoints();
    return await apiRequest<ICorporateOk>(
        endpoints.updateCorporateUsers(corporateId),
        "PUT",
        { body: JSON.stringify(update) }
    );
}

/** Filters for the shared booking search (see api/postCustomBookingsSearch). */
export interface ICompanyBookingFilters {
    /** Scopes the search to this company. */
    corporateAgreementReference?: string;
    /** PNR. */
    reference?: string;
    purchaserPassengerName?: string;
    /** DateRange is { startTime, endDate } in the contract — not from/to, and
     *  additionalProperties is false, so the wrong keys are a 400. */
    travelDateRange?: { startTime?: string; endDate?: string };
}
